import { useEffect, useId } from "react";
import type { ReactNode } from "react";
import { publish } from "@/components/base/toastStore";

/**
 * 버튼을 누른 결과를 알리는 한 줄.
 *
 * **왜 색과 테두리를 쓰나** — 예전에는 성공·경고·실패가 전부 `text-xs text-gray-900` 한 줄로
 * 나왔다. 담당자가 QA 중에 *"버튼을 눌렀는데 결과가 눈에 띄지 않는다"* 고 지적했고,
 * 2026-09-09 에 상태색 도입을 결정했다. 이 프로젝트에서 **색이 무채색 규칙을 벗어나는 유일한
 * 자리**다(CLAUDE.md §UI 규칙 1·2 의 예외).
 *
 * 경고는 본문과 **줄을 나눠** 그린다. 예전에는 성공 문구와 경고를 `join(" ")` 으로 한 문장에
 * 이어 붙여서 어디까지가 성공이고 어디부터가 경고인지 읽히지 않았다.
 *
 * ---
 *
 * **2026-10-02 — 누른 결과는 흐름 밖으로 띄운다.**
 *
 * 이 상자는 `Screen` 의 `heading`·`toolbar`(둘 다 `shrink-0`) 안에 있었고 바깥이
 * `overflow-hidden` 이라, 한 줄(약 49px)이 그대로 **표에서 빠졌다**. 자동으로 사라지지도
 * 않아 *"컨텐츠 영역을 차지한 채 계속 떠 있다"* 는 지적을 받았다. 상자와 톤 색은 그대로
 * 두고 **자리만** 옮겼다 — 그리는 곳은 `components/base/ToastLayer.tsx`.
 *
 * **무엇을 띄우고 무엇을 흐름에 남기나** — 이미 두 호출 모양이 그 경계였다.
 *
 * | 모양 | 뜻 | 처리 |
 * |---|---|---|
 * | `value={msg.value}` (`useStatusMessage`) | **버튼을 누른 결과** | 띄운다 |
 * | `tone` + children (`ErrorText`·기사 `Notice`·`query.isError` 분기) | **그 화면의 지금 상태** | 흐름 안 |
 *
 * 예외는 `value` 모양이면서 *결과가 아니라 그 건의 신원*인 둘이다 — `warnNotice()`·
 * `statusNotice()` 가 만드는 머리말 경고(송장 검수의 미분류·재제출·중복·반려, 자사정보의
 * 기본값 안내). 그 팩토리가 `inline: true` 를 심어 둔다. **훅이 만든 것은 클릭을 알리고,
 * 팩토리가 만든 것은 레코드를 설명한다.**
 *
 * **발행하는 자리는 그 동작이 끝날 때까지 마운트돼 있어야 한다.** 알림의 수명은
 * `toastStore` 가 들고 있어 모달이 닫혀도 남지만, 발행은 이 컴포넌트의 `useEffect` 가
 * 한다 — 성공이 폼을 닫는 **같은 렌더**에서 이 자리가 언마운트되면 발행이 일어나지
 * 않아 결과가 아무 데도 안 뜬다. 그래서 닫히는 폼·패널 안에는 두지 않는다.
 * 모달에서 성공하는 화면 대부분은 이미 `onDone`·`onCreated` 로 부모에게 문구를 넘긴다
 * (`QuickCreatePanel`·`ValidationDetail`·`CreateForm`) — 그 길을 따른다.
 */

export type MessageTone = "success" | "warn" | "error" | "info";

export interface StatusMessageValue {
  tone: MessageTone;
  text: ReactNode;
  /** 본문 아래 줄로 그릴 부가 문구. 성공했지만 짚어 둘 것이 있을 때 */
  warnings?: string[];
  /**
   * 띄우지 않고 흐름 안에 그린다.
   *
   * 누른 결과가 아니라 **그 건의 지금 상태**를 적은 문구에만 쓴다 — 머리말 경고
   * 팩토리(`warnNotice`·`statusNotice`)가 심는다. 떠 있는 알림은 4초 뒤 사라지거나
   * 담당자가 내리는 것이라, 화면을 열어 둔 동안 계속 보여야 하는 문구를 담을 수 없다.
   */
  inline?: boolean;
}

/**
 * 테두리·글자색. `ToastLayer` 가 같은 값을 쓴다 — 두 자리의 톤이 갈리면
 * 같은 결과가 자리에 따라 다른 색으로 보인다.
 *
 * **면은 칠하지 않는다.** 바탕을 칠하면 표의 선택 상태(`primary-50`)·주 동작 버튼과 경쟁해
 * 화면에서 가장 강한 것이 무엇인지 흐려진다. 테두리와 글자색까지만 쓴다.
 * (떠 있는 알림은 컨텐츠 평면 밖이라 경쟁이 없고, 대신 글자가 표 위에 겹치므로
 * `ToastLayer` 가 거기서만 `bg-white` 를 얹는다.)
 */
export const TONE: Record<MessageTone, string> = {
  /** 뜻대로 됐다 */
  success: "border-primary-600 text-primary-700",
  /** 됐지만 짚어 볼 것이 있다 */
  warn: "border-amber-500 text-amber-700",
  /** 안 됐다 */
  error: "border-red-500 text-red-600",
  /** 상태를 알릴 뿐이다 */
  info: "border-gray-300 text-gray-700",
};

/**
 * `value` 로 톤과 문구를 함께 받거나(`useStatusMessage`), 톤이 고정인 자리에서는
 * `tone` + 자식으로 쓴다 — 조회 실패처럼 언제나 실패인 곳이 그렇다.
 */
export default function StatusMessage({
  value,
  tone = "info",
  children,
  className = "",
  inline = false,
}: {
  value?: StatusMessageValue | null;
  tone?: MessageTone;
  children?: ReactNode;
  className?: string;
  /** 누른 결과라도 흐름 안에 그린다 — 로그인 폼처럼 밀 것이 없고 입력칸에 붙어야 하는 자리 */
  inline?: boolean;
}) {
  const shown: StatusMessageValue | null = value ?? (children ? { tone, text: children } : null);
  const empty = !shown || (!shown.text && !shown.warnings?.length);

  // 띄우는 것은 `value` **칸으로 들어오는 자리**다 — `tone` + children 은 그 화면의
  // 지금 상태라 흐름 안에 남는다.
  //
  // 판정을 `!!value` 가 아니라 `value !== undefined` 로 하는 이유: `msg.clear()` 는
  // `value` 를 `null` 로 되돌린다. 값으로 보면 그 순간 "띄우는 자리" 가 아니게 되어
  // **내리라는 발행을 못 하고 알림이 타이머까지 남는다.**
  const floats = value !== undefined && !inline && !value?.inline;

  // `useId` 는 **렌더 지점마다** 다르다 — 같은 `msg` 를 두 자리에 그리면 상자가 둘 뜬다.
  // (그래서 기사 화면의 중복 렌더 두 곳을 지웠다. 흐름 안에 있던 시절 스크롤 밖으로
  //  밀려 안 보이는 것을 막으려고 섹션마다 한 번 더 그려 둔 것들이다.)
  const key = useId();
  useEffect(() => {
    if (!floats) return;
    publish(key, empty ? null : shown);
    // 언마운트에서 거두지 않는다 — 성공으로 모달이 닫혀도 알림은 남아야 한다.
    // 수명은 `toastStore` 가 톤에 따라 들고 있다.
  }, [floats, key, empty, shown]);

  if (floats || !shown || empty) return null;
  return (
    <div
      role="status"
      className={`mb-3 rounded border px-3 py-2 text-xs leading-relaxed ${TONE[shown.tone]} ${className}`}
    >
      {shown.text}
      {shown.warnings?.map((warning) => (
        <p key={warning} className="mt-1">
          {warning}
        </p>
      ))}
    </div>
  );
}
